import { Alert, AlertIcon, BoxProps, Button, Checkbox, Flex, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getTenantId } from '@pelicargo/tenants';
import { DimensionUnit, WeightUnit } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { dimensionUnitOptions, PermissionLevel, weightUnitOptions } from '@pelicargo/utils';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HiArrowRight, HiOutlineClipboardDocumentList } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { useContacts } from '../../hooks/contact/useContacts';
import { useModEnter } from '../../hooks/useModEnter';
import { Contact, Request } from '../../utils/types';
import { SimpleCheckbox } from '../SimpleCheckbox';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleTextarea } from '../SimpleTextarea';

type QuoteFormProps = BoxProps & {
  onSubmit: (values: any) => void;
  onClose?: () => void;
  isLoading?: boolean;
  request?: Partial<Request> & { customMessage?: string };
  sendLongerEmail?: boolean;
  canSend: boolean;
  hasCustomerEmail?: boolean;
  requireSpotConfirmation?: boolean;
};

type Values = {
  contentOnly?: boolean;
  clipboard?: boolean;
  originAirportIds: string;
  destinationAirportIds: string;
  preferredWeightUnit: WeightUnit;
  preferredDimensionUnit: DimensionUnit;
  senderContact: Partial<Contact>;
  senderContactId: number;
  customMessage: string;
  isLongEmail: boolean;
};

const validationSchema = z.object({
  originAirportIds: z.string().nullish(),
  destinationAirportIds: z.string().nullish(),
  preferredWeightUnit: z.enum([WeightUnit.lb, WeightUnit.kg]),
  preferredDimensionUnit: z.enum([DimensionUnit.cm, DimensionUnit.in]),
  senderContactId: z.coerce.number(),
  customMessage: z.string().nullish(),
  isLongEmail: z.boolean().nullish(),
  clipboard: z.boolean().nullish(),
  contentOnly: z.boolean().optional().default(false),
});

export const transformToClient = (request: Partial<Request & { customMessage?: string }> = {}) =>
  ({
    originAirportIds: request?.booked_quote_variant?.quote?.origin_airport_id ?? request?.origin_airports?.[0]?.id,
    destinationAirportIds:
      request?.booked_quote_variant?.quote?.destination_airport_id ??
      request?.destination_airports?.[request?.destination_airports?.length - 1]?.id,
    preferredWeightUnit: WeightUnit.kg,
    preferredDimensionUnit: DimensionUnit.in,
    customMessage: request?.customMessage,
    isLongEmail: false,
    clipboard: false,
  }) satisfies Partial<Values>;

export const EmailQuoteForm = ({
  sendLongerEmail,
  request,
  onSubmit,
  onClose,
  isLoading,
  canSend,
  hasCustomerEmail,
  requireSpotConfirmation,
}: QuoteFormProps) => {
  const { user } = useAuth();
  const [isSendDisabled, setIsSendDisabled] = useState(!canSend);
  const [spotConfirmed, setSpotConfirmed] = useState(false);
  const defaultValues = useMemo(() => transformToClient(request), [request]);
  const [contacts] = useContacts({ minPermission: PermissionLevel.CSMEMBER, tenant: request.tenant_id });
  const { data: currentTenant } = trpc.getTenant.useQuery({
    id: request.tenant_id,
  });
  const grayWhite = useColorModeValue('gray.600', 'white');

  const { setValue, ...methods } = useForm<Values>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
    defaultValues,
  });

  const senderContactId = methods.watch('senderContactId');
  const [isCopying, setIsCopying] = useState(false);

  const defaultContact = useMemo(() => {
    return contacts?.find((contact) => contact?.email === user?.email);
  }, [contacts, user?.email]);
  const shouldImpersonate = useMemo(() => {
    return currentTenant?.impersonation_contact_id && getTenantId() !== request.tenant_id;
  }, [currentTenant?.impersonation_contact_id, request.tenant_id]);

  useEffect(() => {
    if (defaultContact?.id) {
      setValue('senderContactId', defaultContact.id);
    }
    if (shouldImpersonate) {
      setValue('senderContactId', currentTenant?.impersonation_contact_id);
    }
  }, [currentTenant?.id, currentTenant?.impersonation_contact_id, defaultContact?.id, setValue, shouldImpersonate]);

  useEffect(() => {
    const isDisabled = !(!!senderContactId && canSend) || (!!requireSpotConfirmation && !spotConfirmed);
    setIsSendDisabled(isDisabled);
  }, [canSend, senderContactId, requireSpotConfirmation, spotConfirmed]);

  const handleCancelSelected = () => onClose?.();

  const setClipboard = async (contentOnly: boolean) => {
    setIsCopying(true);
    setValue('contentOnly', contentOnly);
    setValue('clipboard', true);
    await methods.handleSubmit(onSubmit)();
    setValue('clipboard', false);
    setValue('contentOnly', false);
    setIsCopying(false);
  };

  useEffect(() => {
    setValue('customMessage', request.customMessage);
  }, [request.customMessage, setValue]);

  const submitRef = useModEnter();

  return (
    <FormProvider setValue={setValue} {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack flex={1}>
              <SimpleInput size="md" name="originAirportIds" placeholder="LAX" label="Origin" />
            </Stack>
            <HiArrowRight />
            <Stack flex={1}>
              <SimpleInput size="md" name="destinationAirportIds" placeholder="LHR" label="Destination" />
            </Stack>
            <Stack flex={1}>
              <SimpleSelect name="preferredWeightUnit" label="Weight unit" options={weightUnitOptions} />
            </Stack>
            <Stack flex={1}>
              <SimpleSelect name="preferredDimensionUnit" label="Dimension unit" options={dimensionUnitOptions} />
            </Stack>
          </Stack>

          <SimpleSelect
            name="senderContactId"
            label="From contact"
            helper={shouldImpersonate ? "Sending to Tenant's customer" : ''}
            options={[
              ...(shouldImpersonate
                ? [
                    {
                      label: `${currentTenant?.impersonation_contact?.full_name} <${currentTenant?.impersonation_contact?.email}>`,
                      value: currentTenant?.impersonation_contact?.id,
                    },
                  ]
                : contacts?.map((contact) => ({
                    label: `${contact?.full_name} <${contact?.email}>`,
                    value: contact.id,
                  })) ?? []),
            ]}
          />

          <SimpleTextarea placeholder="Message" size={['sm', 'md', 'lg', 'xl']} name="customMessage" />

          <Show if={!!sendLongerEmail}>
            <SimpleCheckbox name="isLongEmail" label="Send a longer email" />
          </Show>

          <Stack direction="column" spacing={4} py={4}>
            <Show if={!!requireSpotConfirmation}>
              <Alert status="warning" borderRadius="md" alignItems="flex-start" maxW="lg">
                <AlertIcon />
                <Stack spacing={2}>
                  <Text fontWeight="semibold">This is a spot rate</Text>
                  <Text fontSize="sm">
                    Spot rates must be confirmed with the carrier, otherwise they bill the standard rate. Confirm you
                    have accepted this rate with the carrier before finalizing.
                  </Text>
                  <Checkbox isChecked={spotConfirmed} onChange={(event) => setSpotConfirmed(event.target.checked)}>
                    I have confirmed the spot rate with the carrier
                  </Checkbox>
                </Stack>
              </Alert>
            </Show>
            <Show if={!hasCustomerEmail}>
              <Flex color="orange.500">Only Pelicargo emails are specified</Flex>
            </Show>
            <Stack spacing={3} direction="row" justify="flex-end" w="full">
              <Button color={grayWhite} variant="ghost" onClick={handleCancelSelected}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                isLoading={isLoading && isCopying}
                rightIcon={<HiOutlineClipboardDocumentList />}
                onClick={() => setClipboard(true)}
              >
                Copy (Body Only)
              </Button>
              <Button
                colorScheme="blue"
                isLoading={isLoading && isCopying}
                rightIcon={<HiOutlineClipboardDocumentList />}
                onClick={() => setClipboard(false)}
              >
                Copy
              </Button>
              <Button
                colorScheme="brand"
                type="submit"
                ref={submitRef}
                disabled={isSendDisabled}
                isLoading={isLoading && !isCopying}
                rightIcon={<HiArrowRight />}
              >
                Send Email(s)
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  );
};
